import { useEffect, useRef, useState } from "react";
import { Search, Bell, Moon, Sun, Menu } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { useUnreadCount } from "@/hooks/useNotifications";
import { NotificationPanel } from "@/components/notifications/NotificationPanel";

interface TopBarProps {
  title: string;
  onMenuClick: () => void;
}

export const TopBar = ({ title, onMenuClick }: TopBarProps) => {
  const [time, setTime] = useState(new Date());
  const [showPanel, setShowPanel] = useState(false);
  const bellRef = useRef<HTMLDivElement>(null);
  const { theme, toggleTheme } = useTheme();
  const { data: unreadData } = useUnreadCount();
  const unread = unreadData?.count ?? 0;

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);

  const dateStr = time.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const timeStr = time.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return (
    <header className="h-16 bg-card border-b border-border flex items-center justify-between px-4 md:px-6 sticky top-0 z-20">
      <div className="flex items-center gap-3 min-w-0">
        <button
          type="button"
          onClick={onMenuClick}
          className="md:hidden p-2 -ml-2 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5" />
        </button>
        <h1 className="text-base md:text-lg font-semibold text-foreground truncate">
          {title}
        </h1>
      </div>

      <div className="flex items-center gap-2 md:gap-4">
        <span className="text-sm text-muted-foreground hidden md:block">
          {dateStr}, {timeStr}
        </span>
        <div className="relative hidden sm:block">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            placeholder="Search... (Ctrl+K)"
            className="pl-9 pr-4 py-2 text-sm rounded-btn border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 w-40 md:w-56"
          />
        </div>
        <button
          type="button"
          onClick={toggleTheme}
          className="p-2 text-muted-foreground hover:text-foreground transition-colors"
          aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
        >
          {theme === "dark" ? (
            <Sun className="w-5 h-5" />
          ) : (
            <Moon className="w-5 h-5" />
          )}
        </button>

        <div ref={bellRef} className="relative">
          <button
            type="button"
            onClick={() => setShowPanel((v) => !v)}
            className="relative p-2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Notifications"
          >
            <Bell className="w-5 h-5" />
            {unread > 0 && (
              <span className="absolute top-1 right-1 min-w-[16px] h-4 px-0.5 bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </button>
          {showPanel && (
            <NotificationPanel onClose={() => setShowPanel(false)} />
          )}
        </div>
      </div>
    </header>
  );
};

export default TopBar;
